import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Players() {
  const [players, setPlayers] = useState([]);
  const { t } = useLang();

  useEffect(() => {
    axios.get(`${API}/players`).then((r) => setPlayers(r.data)).catch(() => {});
  }, []);

  return (
    <section id="talents" data-testid="players-section" className="py-28 lg:py-40 bg-[#0A0A0C]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-wrap items-end justify-between gap-8 mb-20"
        >
          <div>
            <p className="text-[#C5A059] text-xs uppercase tracking-[0.4em] mb-6">{t("players.eyebrow")}</p>
            <h2 className="font-display text-4xl md:text-5xl uppercase font-bold tracking-tight leading-tight">
              {t("players.title")}
            </h2>
          </div>
          <p className="text-[#71717A] text-sm max-w-sm leading-relaxed">
            {t("players.text")}
          </p>
        </motion.div>

        {players.length === 0 ? (
          <div className="border border-dashed border-[#27272A] p-16 text-center">
            <p className="font-display text-2xl uppercase font-medium text-white">{t("players.empty.t")}</p>
            <p className="text-[#71717A] text-sm mt-3 max-w-md mx-auto leading-relaxed">
              {t("players.empty.x")}
            </p>
          </div>
        ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="players-grid">
          {players.map((p, i) => (
            <motion.article
              key={p.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="group relative aspect-[3/4] overflow-hidden border border-[#27272A] bg-[#18181B]"
              data-testid={`player-card-${i}`}
            >
              <img
                src={p.image}
                alt={p.nom}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-[filter,transform] duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />

              <span className="absolute top-5 left-5 border border-[#C5A059] text-[#C5A059] text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 bg-black/40 backdrop-blur-sm">
                {p.badge}
              </span>

              <div className="absolute bottom-0 inset-x-0 p-6">
                <p className="text-[#C5A059] text-[10px] uppercase tracking-[0.25em]">{p.poste} — {p.age} ans</p>
                <h3 className="font-display text-2xl uppercase font-medium mt-2">{p.nom}</h3>
                <p className="text-[#A1A1AA] text-xs mt-1">{p.club} · {p.nationalite}</p>

                <div className="grid grid-rows-[0fr] group-hover:grid-rows-[1fr] transition-[grid-template-rows] duration-500">
                  <div className="overflow-hidden">
                    <div className="pt-5 space-y-2">
                      {Object.entries(p.stats).map(([k, v]) => (
                        <div key={k} className="flex items-center gap-3">
                          <span className="text-[10px] uppercase tracking-widest text-[#71717A] w-16">{k}</span>
                          <div className="flex-1 h-px bg-[#27272A] relative">
                            <div className="absolute inset-y-0 left-0 bg-[#C5A059]" style={{ width: `${v}%` }} />
                          </div>
                          <span className="text-[10px] text-[#C5A059]">{v}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
        )}
      </div>
    </section>
  );
}
